import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import { supabase, TABLES } from '../../config/supabase'
import Layout from '../../components/layout/Layout'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'
import toast from 'react-hot-toast'

const { FiUsers, FiUser, FiCheck, FiArrowRight, FiArrowLeft, FiStar, FiPlus } = FiIcons

const OnboardingWizard = () => {
  const { user, completeOnboarding } = useAuth()
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)

  // Step data
  const [familyData, setFamilyData] = useState({ name: '' })
  const [children, setChildren] = useState([])
  const [currentChild, setCurrentChild] = useState({ name: '', age: '', avatar: '👦' })
  const [firstTask, setFirstTask] = useState({
    title: '',
    description: '',
    points: 10,
    category: 'cleaning'
  })

  const avatarOptions = [
    // Basic children - various ethnicities
    '👦', '👧', '🧒', '👶',
    // Light skin tone
    '👦🏻', '👧🏻', '🧒🏻', '👶🏻',
    // Medium-light skin tone
    '👦🏼', '👧🏼', '🧒🏼', '👶🏼',
    // Medium skin tone
    '👦🏽', '👧🏽', '🧒🏽', '👶🏽',
    // Medium-dark skin tone
    '👦🏾', '👧🏾', '🧒🏾', '👶🏾',
    // Dark skin tone
    '👦🏿', '👧🏿', '🧒🏿', '👶🏿',
    // Superheroes - Male (various skin tones)
    '🦸‍♂️', '🦸🏻‍♂️', '🦸🏼‍♂️', '🦸🏽‍♂️', '🦸🏾‍♂️', '🦸🏿‍♂️',
    // Superheroes - Female (various skin tones)
    '🦸‍♀️', '🦸🏻‍♀️', '🦸🏼‍♀️', '🦸🏽‍♀️', '🦸🏾‍♀️', '🦸🏿‍♀️',
    // Supervillains - Male (various skin tones)
    '🦹‍♂️', '🦹🏻‍♂️', '🦹🏼‍♂️', '🦹🏽‍♂️', '🦹🏾‍♂️', '🦹🏿‍♂️',
    // Supervillains - Female (various skin tones)
    '🦹‍♀️', '🦹🏻‍♀️', '🦹🏼‍♀️', '🦹🏽‍♀️', '🦹🏾‍♀️', '🦹🏿‍♀️',
    // Royalty - various skin tones
    '🤴', '🤴🏻', '🤴🏼', '🤴🏽', '🤴🏾', '🤴🏿',
    '👸', '👸🏻', '👸🏼', '👸🏽', '👸🏾', '👸🏿',
    // Mages/Wizards - various skin tones
    '🧙‍♂️', '🧙🏻‍♂️', '🧙🏼‍♂️', '🧙🏽‍♂️', '🧙🏾‍♂️', '🧙🏿‍♂️',
    '🧙‍♀️', '🧙🏻‍♀️', '🧙🏼‍♀️', '🧙🏽‍♀️', '🧙🏾‍♀️', '🧙🏿‍♀️',
    // Genies
    '🧞‍♂️', '🧞‍♀️',
    // Elves - various skin tones
    '🧝‍♂️', '🧝🏻‍♂️', '🧝🏼‍♂️', '🧝🏽‍♂️', '🧝🏾‍♂️', '🧝🏿‍♂️',
    '🧝‍♀️', '🧝🏻‍♀️', '🧝🏼‍♀️', '🧝🏽‍♀️', '🧝🏾‍♀️', '🧝🏿‍♀️',
    // Fairies - various skin tones
    '🧚‍♂️', '🧚🏻‍♂️', '🧚🏼‍♂️', '🧚🏽‍♂️', '🧚🏾‍♂️', '🧚🏿‍♂️',
    '🧚‍♀️', '🧚🏻‍♀️', '🧚🏼‍♀️', '🧚🏽‍♀️', '🧚🏾‍♀️', '🧚🏿‍♀️',
    // Vampires - various skin tones
    '🧛‍♂️', '🧛🏻‍♂️', '🧛🏼‍♂️', '🧛🏽‍♂️', '🧛🏾‍♂️', '🧛🏿‍♂️',
    '🧛‍♀️', '🧛🏻‍♀️', '🧛🏼‍♀️', '🧛🏽‍♀️', '🧛🏾‍♀️', '🧛🏿‍♀️',
    // Fun characters
    '🦄', '🌟', '🚀', '⭐', '🎨', '🎵', '📚', '⚽'
  ]

  const sampleTasks = [
    { title: 'Make Your Bed', category: 'cleaning', points: 10, emoji: '🛏️' },
    { title: 'Brush Your Teeth', category: 'other', points: 5, emoji: '🦷' },
    { title: 'Put Away Toys', category: 'cleaning', points: 15, emoji: '🧸' },
    { title: 'Feed the Pet', category: 'pets', points: 10, emoji: '🐕' },
    { title: 'Load Dishwasher', category: 'dishes', points: 15, emoji: '🍽️' },
    { title: 'Take Out Trash', category: 'cleaning', points: 20, emoji: '🗑️' }
  ]

  const steps = [
    { id: 1, title: 'Welcome to Chore Haven!', subtitle: 'Let\'s set up your family account', component: 'welcome' },
    { id: 2, title: 'Create Your Family', subtitle: 'What should we call your family?', component: 'family' },
    { id: 3, title: 'Add Your Children', subtitle: 'Add the kids who will be doing chores', component: 'children' },
    { id: 4, title: 'Create Your First Task', subtitle: 'Start with a simple chore to get going', component: 'task' },
    { id: 5, title: 'You\'re All Set!', subtitle: 'Your family adventure begins now', component: 'complete' }
  ]

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleAddChild = () => {
    if (currentChild.name.trim()) {
      setChildren([...children, { ...currentChild, id: Date.now() }])
      setCurrentChild({ name: '', age: '', avatar: '👦' })
      toast.success(`${currentChild.name} added to the family!`)
    }
  }

  const handleRemoveChild = (childId) => {
    setChildren(children.filter(c => c.id !== childId))
  }

  const handleFinishOnboarding = async () => {
    setLoading(true)
    
    try {
      console.log('🎯 Starting onboarding completion process...')
      console.log('👤 User ID:', user.id)
      console.log('👨‍👩‍👧‍👦 Family Name:', familyData.name)
      console.log('👶 Children Count:', children.length)
      
      // 1. Create family
      console.log('📝 Creating family...')
      const { data: family, error: familyError } = await supabase
        .from(TABLES.FAMILIES)
        .insert([{
          name: familyData.name,
          parent_id: user.id,
          created_at: new Date().toISOString()
        }])
        .select()
        .single()

      if (familyError) {
        console.error('❌ Family creation error:', familyError)
        throw familyError
      }

      console.log('✅ Family created successfully:', family.id, family.name)

      // 2. Add children if any
      let createdChildren = []
      if (children.length > 0) {
        console.log('👶 Adding children...')
        const childrenToInsert = children.map(child => ({
          family_id: family.id,
          name: child.name,
          age: child.age ? parseInt(child.age) : null,
          avatar: child.avatar,
          created_at: new Date().toISOString()
        }))

        const { data: childrenData, error: childrenError } = await supabase
          .from(TABLES.CHILDREN)
          .insert(childrenToInsert)
          .select()

        if (childrenError) {
          console.error('❌ Children creation error:', childrenError)
          throw childrenError
        }

        createdChildren = childrenData
        console.log('✅ Children added successfully:', createdChildren.length)
      }

      // 3. Create first task if provided
      if (firstTask.title.trim()) {
        console.log('📋 Creating first task...')
        const { error: taskError } = await supabase
          .from(TABLES.TASKS)
          .insert([{
            family_id: family.id,
            title: firstTask.title,
            description: firstTask.description,
            points: firstTask.points,
            category: firstTask.category,
            recurring_pattern: 'daily',
            is_active: true,
            created_at: new Date().toISOString()
          }])

        if (taskError) {
          console.error('⚠️ Task creation error:', taskError)
          // Don't throw error for task creation failure
          console.log('⚠️ Task creation failed, but continuing...')
        } else {
          console.log('✅ First task created successfully')
        }
      }

      // 4. Build complete family object with children
      const completeFamilyData = {
        ...family,
        children: createdChildren
      }

      console.log('📋 Complete family data prepared:', {
        id: completeFamilyData.id,
        name: completeFamilyData.name,
        childrenCount: completeFamilyData.children.length
      })

      // 5. Complete onboarding process BEFORE navigation
      console.log('🏁 Completing onboarding process...')
      const onboardingSuccess = await completeOnboarding(completeFamilyData)
      
      if (onboardingSuccess) {
        console.log('🎉 Onboarding completed successfully!')
        toast.success('🎉 Welcome to Chore Haven! Your family is all set up!')
        
        // Small delay to ensure state is fully updated
        setTimeout(() => {
          console.log('🚀 Navigating to dashboard...')
          console.log('🔄 Current state - needsOnboarding should be false')
          navigate('/dashboard', { replace: true })
        }, 500) // Reduced delay but still ensures state update
      } else {
        throw new Error('Failed to complete onboarding process')
      }
      
    } catch (error) {
      console.error('❌ Onboarding error:', error)
      toast.error('There was an error setting up your family. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const renderWelcomeStep = () => (
    <div className="text-center">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
        className="text-8xl mb-6"
      >
        🎉
      </motion.div>
      <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
        Welcome to Chore Haven!
      </h2>
      <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
        You've successfully verified your email! Now let's set up your family account and start turning chores into fun adventures.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="text-center">
          <div className="text-4xl mb-2">👨‍👩‍👧‍👦</div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Create Your Family</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Set up your family profile</p>
        </div>
        <div className="text-center">
          <div className="text-4xl mb-2">⭐</div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Add Tasks & Rewards</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Create fun chores and prizes</p>
        </div>
        <div className="text-center">
          <div className="text-4xl mb-2">🏆</div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Watch Progress</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Track achievements together</p>
        </div>
      </div>
    </div>
  )

  const renderFamilyStep = () => (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">👨‍👩‍👧‍👦</div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          What's your family name?
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          This will be displayed throughout the app
        </p>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Family Name
          </label>
          <input
            type="text"
            value={familyData.name}
            onChange={(e) => setFamilyData({ ...familyData, name: e.target.value })}
            placeholder="e.g., The Smith Family"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center text-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            autoFocus
          />
        </div>
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          <p>💡 Tip: You can always change this later in settings</p>
        </div>
      </div>
    </div>
  )

  const renderChildrenStep = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">👦👧</div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Add Your Children
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Add the kids who will be participating in chores
        </p>
      </div>

      {/* Added Children */}
      {children.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Your Kids:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {children.map((child) => (
              <motion.div
                key={child.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800"
              >
                <div className="text-2xl">{child.avatar}</div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{child.name}</div>
                  {child.age && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">Age {child.age}</div>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveChild(child.id)}
                  className="text-gray-400 hover:text-red-500 p-1"
                >
                  ×
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Add Child Form */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {children.length === 0 ? 'Add Your First Child' : 'Add Another Child'}
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Child's Name *
              </label>
              <input
                type="text"
                value={currentChild.name}
                onChange={(e) => setCurrentChild({ ...currentChild, name: e.target.value })}
                placeholder="Enter name"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Age (Optional)
              </label>
              <input
                type="number"
                value={currentChild.age}
                onChange={(e) => setCurrentChild({ ...currentChild, age: e.target.value })}
                placeholder="Age"
                min="1"
                max="18"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Choose Avatar
            </label>
            <div className="grid grid-cols-6 gap-2 max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              {avatarOptions.map((avatar, index) => (
                <button
                  key={`${avatar}-${index}`}
                  type="button"
                  onClick={() => setCurrentChild({ ...currentChild, avatar })}
                  className={`
                    p-2 rounded-lg border-2 transition-all duration-200 text-xl hover:scale-110
                    ${currentChild.avatar === avatar
                      ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  {avatar}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Includes diverse superheroes, royalty, magical characters, and more!
            </p>
          </div>
          <Button
            onClick={handleAddChild}
            disabled={!currentChild.name.trim()}
            variant="primary"
            className="w-full"
          >
            <SafeIcon icon={FiPlus} />
            Add {currentChild.name || 'Child'}
          </Button>
        </div>
      </Card>

      {children.length === 0 && (
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            💡 You can skip this step and add children later if needed
          </p>
        </div>
      )}
    </div>
  )

  const renderTaskStep = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">⭐</div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Create Your First Task
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Start with a simple chore to get the family motivated
        </p>
      </div>

      {/* Quick Task Options */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Quick Start Options:</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {sampleTasks.map((task, index) => (
            <motion.button
              key={index}
              onClick={() => setFirstTask({
                title: task.title,
                description: `A simple daily task worth ${task.points} points`,
                points: task.points,
                category: task.category
              })}
              className={`
                p-4 rounded-lg border-2 transition-all duration-200 text-center hover:scale-105
                ${firstTask.title === task.title
                  ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }
              `}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="text-2xl mb-1">{task.emoji}</div>
              <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{task.title}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{task.points} points</div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Custom Task Form */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Or Create a Custom Task:
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Task Title
            </label>
            <input
              type="text"
              value={firstTask.title}
              onChange={(e) => setFirstTask({ ...firstTask, title: e.target.value })}
              placeholder="e.g., Make your bed"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={firstTask.description}
              onChange={(e) => setFirstTask({ ...firstTask, description: e.target.value })}
              placeholder="Optional details about the task..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Points
              </label>
              <input
                type="number"
                value={firstTask.points}
                onChange={(e) => setFirstTask({ ...firstTask, points: parseInt(e.target.value) || 10 })}
                min="1"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category
              </label>
              <select
                value={firstTask.category}
                onChange={(e) => setFirstTask({ ...firstTask, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="cleaning">Cleaning</option>
                <option value="dishes">Dishes</option>
                <option value="laundry">Laundry</option>
                <option value="pets">Pet Care</option>
                <option value="homework">Homework</option>
                <option value="outdoor">Outdoor</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>
      </Card>

      <div className="text-center mt-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          💡 Don't worry, you can add more tasks and customize everything later!
        </p>
      </div>
    </div>
  )

  const renderCompleteStep = () => (
    <div className="text-center">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
        className="text-8xl mb-6"
      >
        🎊
      </motion.div>
      <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
        Congratulations!
      </h2>
      <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
        Your family account is all set up! You're ready to start your chore adventure on chorehaven.com.
      </p>
      
      <div className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-xl p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">What's Next?</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiCheck} className="text-success-500" />
            <span className="text-gray-700 dark:text-gray-300">Family created: {familyData.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiCheck} className="text-success-500" />
            <span className="text-gray-700 dark:text-gray-300">{children.length} children added</span>
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiCheck} className="text-success-500" />
            <span className="text-gray-700 dark:text-gray-300">{firstTask.title ? 'First task created' : 'Ready for tasks'}</span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <p className="text-gray-600 dark:text-gray-400">
          🎯 <strong>Pro Tip:</strong> Start with simple, daily tasks to build momentum!
        </p>
        <p className="text-gray-600 dark:text-gray-400">
          🏆 <strong>Remember:</strong> Celebrate small wins and keep it fun!
        </p>
      </div>
    </div>
  )

  const renderCurrentStep = () => {
    const step = steps.find(s => s.id === currentStep)
    
    switch (step.component) {
      case 'welcome':
        return renderWelcomeStep()
      case 'family':
        return renderFamilyStep()
      case 'children':
        return renderChildrenStep()
      case 'task':
        return renderTaskStep()
      case 'complete':
        return renderCompleteStep()
      default:
        return null
    }
  }

  const canProceed = () => {
    switch (currentStep) {
      case 1: return true // Welcome step
      case 2: return familyData.name.trim().length >= 2
      case 3: return true // Children step is optional
      case 4: return true // Task step is optional
      case 5: return true // Complete step
      default: return false
    }
  }

  return (
    <Layout showNavbar={false}>
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Family Setup</h1>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Step {currentStep} of {steps.length}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <motion.div
                className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${(currentStep / steps.length) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Step Content */}
          <Card className="p-8 mb-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderCurrentStep()}
              </motion.div>
            </AnimatePresence>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <div>
              {currentStep > 1 && (
                <Button
                  variant="ghost"
                  onClick={handlePrevious}
                  className="flex items-center gap-2"
                  disabled={loading}
                >
                  <SafeIcon icon={FiArrowLeft} />
                  Previous
                </Button>
              )}
            </div>

            <div>
              {currentStep < steps.length ? (
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={!canProceed() || loading}
                  className="flex items-center gap-2"
                >
                  Next
                  <SafeIcon icon={FiArrowRight} />
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleFinishOnboarding}
                  loading={loading}
                  className="flex items-center gap-2 text-lg px-8 py-3"
                  disabled={loading}
                >
                  <SafeIcon icon={FiStar} />
                  {loading ? 'Setting up your family...' : 'Start Your Adventure!'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default OnboardingWizard